import * as React from "react";
import { Alert, AlertTitle, Card, CardHeader } from "@mui/material";
import { getErrorMsg } from "../../../../utilities/rtkQueryErrorHelpers";
import ComponentLoader from "../../../common/componentLoader";
import { useSearchJournalEntriesMutation } from "../../../../services/cclTokenedJournalEntryApi";
import { JournalEntry } from "../../../../services/types/journalEntryTypes";
import HistoryTimeLine from "../../../common/historyTimeline/historyTimeline";
import CclButton from "../../../common/cclButtons/cclButton";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../../services/currentUserService/currentUserService";

interface IndividualJournalEntryTimelineCardProps {
    emailAddress: string;
    imKey: string;
    onCreateJournalEntry: () => void;
    onlyIncludeRegistrationEntries?: boolean;
    onlyIncludePayrollEntries?: boolean;
    esiKey?: string;
    payrollKey?: string;
    note?: boolean;
    payroll?: boolean;
    maxWidth?: boolean;
}

const IndividualJournalEntryTimelineCard: React.FC<IndividualJournalEntryTimelineCardProps> = (
    props
) => {
    const [allJournalEntries, setAllJournalEntries] = React.useState<JournalEntry[]>([]);
    const [
        runSearchJournalEntries,
        {
            isLoading: journalEntriesLoading,
            isError: journalEntriesIsError,
            error: journalEntriesError,
        },
    ] = useSearchJournalEntriesMutation();
    const claimsService = new UserClaimsService();
    const allowJournalEntries = claimsService.IsUserInGroups([
        eGroupIds.eClientAdmin,
        eGroupIds.eNWAAdmin,
        eGroupIds.eCCLAdmin,
        eGroupIds.eCCLSuperAdmin,
    ]);

    React.useEffect(() => {
        let entries: JournalEntry[] = [];
        const fieldValue = props.onlyIncludeRegistrationEntries ? props.esiKey : props.payrollKey ? String(props.payrollKey) : props.imKey;
        if (!fieldValue) return;

        runSearchJournalEntries({
            journalCategoryIds: props.onlyIncludeRegistrationEntries ? [3] : props.onlyIncludePayrollEntries ? [6] : [5], // resource journal entry
            categoryDataFieldName: props.onlyIncludeRegistrationEntries ? "esiKey" :  props.payrollKey ? "payrollKey" : "userId",
            categoryDataFieldValue: fieldValue,
        })
            .unwrap()
            .then((journalentries: JournalEntry[]) => {
                entries = [...journalentries];
                if (props.payrollKey) {
                    entries = entries.filter((entry) => {
                        try {
                            const categoryData = JSON.parse(entry.categoryData) as { payrollKey?: string };
                            return String(categoryData.payrollKey ?? "") === String(props.payrollKey);
                        } catch {
                            return entry.categoryData === String(props.payrollKey);
                        }
                    });
                }
                setAllJournalEntries(entries);
            })
            .catch(() => {
                setAllJournalEntries(entries);
            });
    }, [props, runSearchJournalEntries]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {msg}
                </Alert>
            </div>
        );
    }

    if (journalEntriesIsError) {
        return showErrorAlert(getErrorMsg(journalEntriesError));
    }

    const buttonText = () => {
        let text = "";
        if (props.payroll) {
            text = "Add Note";
            return text;
        }
        if (props.note) {
            text = "Create Note";
        } else {
            text = "Create Journal Entry";
        }
        return text;
    }

    return (
        <React.Fragment>
            <Card sx={{ height: 1, width: props.maxWidth ? { xs: 1, lg: 0.8, xl: 0.6 } : .99, m: 2, p: 2 }}>
                {allowJournalEntries ? (
                    <CardHeader
                        action={
                            <CclButton mode={"primary"} onClick={props.onCreateJournalEntry}>
                                {buttonText()}
                            </CclButton>
                        }
                    />
                ) : null}
                {journalEntriesLoading ? (
                    <ComponentLoader msg="Loading" />
                ) : (
                    <HistoryTimeLine events={[]} journalEntries={allJournalEntries} />
                )}
            </Card>
        </React.Fragment>
    );
};

export default IndividualJournalEntryTimelineCard;
